/**
 * CanvasInner - Main canvas orchestrator
 * Manages graph state, drag-drop, undo/redo, save/publish, and renders all panels/modals.
 */

import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconRocket,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconCheck,
  IconSettings,
  IconTrash,
  IconPlayerPlay,
  IconPlugConnected,
  IconBook2,
  IconFileText,
} from "@tabler/icons-react";

// Canvas modules
import { nodeTypes, edgeTypes } from "./constants/nodeTypeRegistry";
import getInitialNodes from "./utils/getInitialNodes";
import useUndoRedo from "./hooks/useUndoRedo";
import Sidebar from "./components/Sidebar";
import ConfigPanel from "./components/ConfigPanel";

// Existing components
import AgentEditorPanel from "../components/AgentEditorPanel";
import AgentSimulator from "../components/AgentSimulator";
import WelcomeAgentModal from "../components/WelcomeAgentModal";
import AgentCreationWizard from "../components/AgentCreationWizard/AgentCreationWizard";
import AgentChoiceModal from "../components/AgentCreationWizard/AgentChoiceModal";
import PublishChannelModal from "../components/PublishChannelModal";
import UpgradeModal from "../components/UpgradeModal";
import UserGuideModal from "../components/UserGuideModal";
import AgentInstructionsModal from "../components/AgentInstructionsModal";

// Services & Context
import { useAgentsFlow } from "../contexts/AgentsFlowContext";
import UserContext from "../../../contexts/UserContext";
import { useAgentsFlowApi } from "../hooks";
import useProFeature from "../hooks/useProFeature";
import usePublishValidation from "../hooks/usePublishValidation";
import AgentsFlowService from "../services/AgentsFlowService";
import {
  validateGraph,
  wouldCreateBifurcation,
  formatErrorsForDisplay,
} from "../utils/graphValidation";

// Styles
import "../styles/agents-flow-canvas.css";

// =============================================================================
// CAPABILITY CODE MAPPING (Frontend -> Backend)
// =============================================================================
const CAPABILITY_CODE_MAP = {
  scheduling: "schedule_appointment",
  check_availability: "check_availability",
  cancellation: "cancel_appointment",
  faq: "answer_faq",
  human_handoff: "human_handoff",
  data_collection: "collect_data",
  qualify_lead: "qualify_lead",
  notifications: "appointment_reminder",
  presence_confirmation: "presence_confirmation",
  followup_auto: "auto_followup",
  sales: "show_catalog",
  delivery_tracking: "delivery_status",
  knowledge_search: "knowledge_search",
  media_library: "send_media_library",
  email_monitor: "email_monitor",
  sheets_reader: "sheets_reader",
};

// =============================================================================
// MAIN CANVAS
// =============================================================================
const CanvasInner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  const { screenToFlowPosition, fitView } = useReactFlow();
  const { getUserPlan } = useContext(UserContext);

  // Context - Fonte única de dados do agente
  const { state, setEditorData, setCurrentAgent, toggleSimulator, highlightedNodeId, setHighlightedNodeId } =
    useAgentsFlow();
  const { editor, simulator } = state;

  // State
  const [nodes, setNodes] = useState(() => getInitialNodes());
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [agentInfo, setAgentInfo] = useState({
    name: "Novo Agente",
    businessType: "",
    wabottyAccount: null,
    isPublished: false,
  });
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [channelModalMode, setChannelModalMode] = useState("publish");
  const [showEditorPanel, setShowEditorPanel] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(!id);
  const [showWizard, setShowWizard] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // PRO feature gating
  const { isProPlan, upgradeModal, openUpgradeModal, closeUpgradeModal } =
    useProFeature();

  // Validação + publish centralizados (compartilhado com PublishChannelModal e ChannelsTab)
  const { validateAndPublish } = usePublishValidation();

  // Context Menu State
  const [contextMenu, setContextMenu] = useState(null);

  // Undo/Redo
  const { takeSnapshot, undo, redo, canUndo, canRedo } = useUndoRedo(
    getInitialNodes(),
    [],
  );

  // Helper: Filter invalid edges
  const filterInvalidEdges = useCallback((edgesToFilter, nodesToCheck) => {
    return edgesToFilter.filter((edge) => {
      if (edge.sourceHandle === "default") {
        const sourceNode = nodesToCheck.find((n) => n.id === edge.source);
        if (
          sourceNode?.type === "condition" &&
          sourceNode?.data?.key !== "intent_router"
        ) {
          return false;
        }
      }
      return true;
    });
  }, []);

  // API
  const api = useAgentsFlowApi();

  // =====================================================
  // HIGHLIGHT DE NÓ — acionado pelo badge no DebugTrace
  // =====================================================
  const highlightTimeoutRef = useRef(null);

  useEffect(() => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);

    setNodes((nds) =>
      nds.map((n) => {
        const base = (n.className || '').replace('node-highlighted', '').trim();
        const cls = n.id === highlightedNodeId ? `${base} node-highlighted`.trim() : base;
        return { ...n, className: cls };
      })
    );

    if (highlightedNodeId) {
      fitView({ nodes: [{ id: highlightedNodeId }], duration: 600, padding: 0.8 });
      highlightTimeoutRef.current = setTimeout(() => setHighlightedNodeId(null), 2500);
    }

    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, [highlightedNodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // =====================================================
  // SINCRONIZAR selectedNode com nodes state
  // =====================================================
  useEffect(() => {
    if (selectedNode) {
      const updatedNode = nodes.find((n) => n.id === selectedNode.id);
      if (updatedNode) {
        const currentDataStr = JSON.stringify(selectedNode.data);
        const updatedDataStr = JSON.stringify(updatedNode.data);
        if (currentDataStr !== updatedDataStr) {
          setSelectedNode(updatedNode);
        }
      }
    }
  }, [nodes, selectedNode]);

  // Load agent if editing
  useEffect(() => {
    if (id) {
      loadAgent();
      setShowWelcomeModal(false);
      setShowWizard(false);
    }
  }, [id]);

  // Handler para quando o agente é criado
  const handleAgentCreated = useCallback(
    (newAgent) => {
      setAgentInfo({
        name: newAgent.name,
        businessType: newAgent.business_info?.business_type || "other",
      });

      setEditorData({
        identity_config: newAgent.identity_config || {},
        business_info: newAgent.business_info || {},
        behavior_config: newAgent.behavior_config || {},
        llm_config: newAgent.llm_config || {},
        capabilities: newAgent.capabilities || [],
        knowledge: [],
        products: [],
        notifications: {},
        advanced_config: {},
      });

      setShowWelcomeModal(false);
      setShowWizard(false);
      setShowChoiceModal(false);

      navigate(`/agents-flow/canvas/${newAgent.id}`, { replace: true });
    },
    [navigate, setEditorData],
  );

  const loadAgent = async () => {
    try {
      const agent = await api.loadAgentById(id);
      if (agent) {
        setAgentInfo({
          name: agent.name,
          businessType: agent.business_info?.business_type || "other",
          wabottyAccount: agent.wabotty_account || null,
          isPublished: !!agent.is_published,
        });

        setEditorData({
          identity_config: agent.identity_config || {},
          business_info: agent.business_info || {},
          behavior_config: agent.behavior_config || {},
          llm_config: agent.llm_config || {
            provider: "openai",
            model: "gpt-4o",
            max_tokens: 4096,
            fallback_enabled: true,
          },
          notifications: agent.notification_configs || {},
          products: agent.products || [],
          capabilities: agent.capabilities || [],
          integrations: agent.integrations || [],
          knowledge: agent.knowledge_sources || [],
          description: agent.description || "",
        });

        const graphDef = agent.graph?.graph_definition;
        if (graphDef?.nodes) {
          // Normaliza nós ao carregar:
          //   - Reaplica deletable=false em start/end (proteção contra
          //     Backspace/Delete; backend pode não persistir esse campo).
          //   - Garante data.key='end' em nós Fim legados — o item da sidebar
          //     usa maxInstances que conta por data.key, então sem isso
          //     agentes antigos aceitariam um segundo nó Fim arrastado.
          //   - Mesma normalização para 'start' por simetria.
          //
          // Auto-restauração de start/end ausentes foi removida intencionalmente:
          // o nó Fim agora é opcional — usuário pode deletar (com confirmação)
          // e re-adicionar arrastando da sidebar de Ações.
          const protectedNodes = graphDef.nodes.map((n) => {
            if (n.type !== "start" && n.type !== "end") return n;
            return {
              ...n,
              deletable: false,
              data: {
                ...(n.data || {}),
                key: n.data?.key || n.type,
              },
            };
          });

          setNodes(protectedNodes);

          const migratedEdges = (graphDef.edges || [])
            .filter((edge) => {
              if (edge.sourceHandle === "default") {
                const sourceNode = graphDef.nodes.find(
                  (n) => n.id === edge.source,
                );
                if (
                  sourceNode?.type === "condition" &&
                  sourceNode?.data?.key !== "intent_router"
                ) {
                  console.warn(
                    `Removing invalid edge from condition node: ${edge.id}`,
                  );
                  return false;
                }
              }
              return true;
            })
            .map((edge) => ({
              ...edge,
              type: "deletable",
            }));
          setEdges(migratedEdges);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar agente:", error);
    }
  };

  // Toggle simulator panel
  const handleToggleSimulator = useCallback(() => {
    if (!simulator.isOpen && id) {
      setCurrentAgent({
        id,
        name: agentInfo.name,
        identity_config: editor.identity_config,
      });
      toggleSimulator(true);
    } else if (simulator.isOpen) {
      toggleSimulator(false);
    }
  }, [
    simulator.isOpen,
    id,
    agentInfo.name,
    editor.identity_config,
    setCurrentAgent,
    toggleSimulator,
  ]);

  // Take snapshot for undo after changes
  const onNodesChangeWithHistory = useCallback(
    (changes) => {
      setNodes((nds) => {
        const newNodes = applyNodeChanges(changes, nds);
        const isSignificant = changes.some(
          (c) =>
            c.type === "remove" ||
            c.type === "add" ||
            (c.type === "position" && c.dragging === false),
        );
        if (isSignificant) {
          setTimeout(() => takeSnapshot(newNodes, edges), 0);
        }
        return newNodes;
      });
    },
    [edges, takeSnapshot],
  );

  const onEdgesChangeWithHistory = useCallback(
    (changes) => {
      setEdges((eds) => {
        const newEdges = applyEdgeChanges(changes, eds);
        const isSignificant = changes.some(
          (c) => c.type === "remove" || c.type === "add",
        );
        if (isSignificant) {
          setTimeout(() => takeSnapshot(nodes, newEdges), 0);
        }
        return newEdges;
      });
    },
    [nodes, takeSnapshot],
  );

  const onConnect = useCallback(
    (params) => {
      const sourceNode = nodes.find((n) => n.id === params.source);

      if (
        sourceNode?.type === "condition" &&
        sourceNode?.data?.key !== "intent_router" &&
        params.sourceHandle === "default"
      ) {
        console.warn(
          "Blocked invalid connection from condition node default handle",
        );
        return;
      }

      if (wouldCreateBifurcation(sourceNode, edges, params.source)) {
        Swal.fire({
          icon: "warning",
          title: "Conexão não permitida",
          text: "Este nó já possui uma conexão de saída. Use um nó de Condição para criar bifurcações.",
          confirmButtonText: "Entendi",
          confirmButtonColor: "#3b82f6",
        });
        return;
      }

      const newEdge = {
        ...params,
        type: "deletable",
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8" },
        style: { stroke: "#94a3b8", strokeWidth: 2 },
      };
      setEdges((eds) => {
        const newEdges = addEdge(newEdge, eds);
        takeSnapshot(nodes, newEdges);
        return newEdges;
      });
    },
    [nodes, edges, takeSnapshot],
  );

  // Drag and drop
  const onDragStart = (event, item) => {
    event.dataTransfer.setData("application/json", JSON.stringify(item));
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const item = JSON.parse(event.dataTransfer.getData("application/json"));
      if (!item) return;

      if (item.maxInstances) {
        const existingCount = nodes.filter(
          (n) => n.data?.key === item.key,
        ).length;
        if (existingCount >= item.maxInstances) {
          Swal.fire({
            icon: "info",
            title: `Limite de "${item.label}"`,
            html:
              item.maxInstancesMessage ||
              `Apenas ${item.maxInstances} nó(s) deste tipo é permitido por fluxo.`,
            confirmButtonColor: "#f59e0b",
          });
          return;
        }
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: `${item.type}-${Date.now()}`,
        type: item.type,
        position,
        data: {
          ...item,
          iconKey: item.key,
        },
        // start/end são fundamentais — protege contra Backspace/Delete acidental
        // (deleção intencional via context menu segue funcionando).
        ...(item.type === "start" || item.type === "end"
          ? { deletable: false }
          : {}),
      };

      setNodes((nds) => {
        const newNodes = [...nds, newNode];
        takeSnapshot(newNodes, edges);
        return newNodes;
      });
    },
    [screenToFlowPosition, edges, takeSnapshot, nodes],
  );

  // Node selection
  const onNodeClick = useCallback((_event, node) => {
    if (node.type !== "start" && node.type !== "end") {
      setSelectedNode(node);
    }
  }, []);

  // Context Menu
  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    // O nó start é fundamental e nunca pode ser deletado. O nó end pode ser
    // deletado pelo menu (com confirmação em handleDeleteNode) — usuário pode
    // querer remover quando faz loop pro roteador ao invés de terminar.
    if (node.type === "start") return;

    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      nodeId: node.id,
      nodeType: node.type,
      nodeLabel: node.data?.label || "Nó",
    });
  }, []);

  const handleDeleteNode = useCallback(async () => {
    if (!contextMenu?.nodeId) return;

    // Snapshot do nó antes de qualquer await — evita race se estado mudar.
    const target = nodes.find((n) => n.id === contextMenu.nodeId);

    // Start nunca pode ser deletado (defesa em profundidade — o context menu
    // já bloqueia o open, mas o estado pode mudar entre abrir e clicar).
    if (target?.type === "start") {
      setContextMenu(null);
      return;
    }

    // Nó Fim exige confirmação explícita — é fácil deletar sem querer e o nó
    // só pode existir uma vez no fluxo.
    if (target?.type === "end") {
      const result = await Swal.fire({
        icon: "warning",
        title: "Remover nó Fim?",
        text: "Você pode adicioná-lo de volta arrastando da sidebar de Ações.",
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#ef4444",
      });
      if (!result.isConfirmed) {
        setContextMenu(null);
        return;
      }
    }

    setNodes((nds) => {
      const newNodes = nds.filter((n) => n.id !== contextMenu.nodeId);
      setEdges((eds) => {
        const newEdges = eds.filter(
          (e) =>
            e.source !== contextMenu.nodeId && e.target !== contextMenu.nodeId,
        );
        takeSnapshot(newNodes, newEdges);
        return newEdges;
      });
      return newNodes;
    });

    setContextMenu(null);
    setSelectedNode(null);
  }, [contextMenu, nodes, takeSnapshot]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === nodeId ? { ...n, data: newData } : n)),
    );
  }, []);

  // Save
  const handleSave = useCallback(async () => {
    if (!agentInfo.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Nome obrigatório",
        text: "Por favor, informe um nome para o agente.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#3b82f6",
      });
      return;
    }

    const validation = validateGraph(nodes, edges, {});

    if (!validation.canSave) {
      // Mostra só os erros que realmente bloqueiam o save (estruturais).
      // Erros de "condition" e "llm" não bloqueiam mais — o de condition vai
      // virar toast pós-save; o de llm aparece só ao publicar.
      const blockingErrors = validation.errors.filter(
        (e) => e.type === "bifurcation" || e.type === "single_instance",
      );
      const errorHtml = formatErrorsForDisplay(blockingErrors);
      Swal.fire({
        icon: "warning",
        title: "Problemas no Fluxo",
        html: errorHtml,
        confirmButtonText: "Entendi",
        confirmButtonColor: "#3b82f6",
      });
      return;
    }

    setIsSaving(true);
    setSaveStatus("saving");

    try {
      const flowData = { nodes, edges };

      const capabilityNodes = nodes.filter((n) => n.type === "capability");
      const capabilitiesForBackend = capabilityNodes.map((n) => ({
        capability_type: CAPABILITY_CODE_MAP[n.data.key] || n.data.key,
        is_active: "true",
        configuration: n.data.config || {},
      }));

      const agentData = {
        name: agentInfo.name,
        description: editor.description || "",
        identity_config: {
          ...editor.identity_config,
          display_name: editor.identity_config?.display_name || agentInfo.name,
        },
        business_info: {
          ...editor.business_info,
          business_type:
            agentInfo.businessType || editor.business_info?.business_type,
        },
        behavior_config: editor.behavior_config || {},
        humanization_config: editor.humanization_config || {},
        llm_config: editor.llm_config || {},
        custom_messages: editor.custom_messages || {},
        agent_contacts: editor.agent_contacts || [],
        experience_level: 3,
      };

      let agentId = id;

      if (id) {
        await api.updateAgent(id, agentData);
      } else {
        const newAgent = await api.createAgent(agentData);
        agentId = newAgent.id;
        navigate(`/agents-flow/canvas/${newAgent.id}`, { replace: true });
      }

      if (agentId) {
        try {
          await AgentsFlowService.updateGraph(agentId, flowData);
        } catch (graphError) {
          console.warn("Erro ao salvar grafo (não crítico):", graphError);
        }
      }

      if (agentId && capabilitiesForBackend.length > 0) {
        await api.toggleCapabilities(agentId, capabilitiesForBackend);
      }

      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 2000);

      // Aviso pós-save: salvamos com sucesso, mas há nós de condição sem
      // todas as ramificações conectadas — o agente não poderá ser publicado
      // até resolver.
      if (!validation.results.conditionEdges.isValid) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "info",
          title: "Salvo com nós incompletos",
          text: "Há nós de condição sem todas as conexões. Resolva antes de publicar.",
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      setSaveStatus("error");

      let errorMessage = "Ocorreu um erro ao salvar o agente. Tente novamente.";
      let isValidationError = false;

      if (error.response?.data) {
        const data = error.response.data;
        if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
          errorMessage = data.non_field_errors.join("\n");
          isValidationError = true;
        } else if (data.detail) {
          errorMessage = data.detail;
          isValidationError = true;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (typeof data === "object") {
          const errors = Object.entries(data)
            .map(([, value]) =>
              Array.isArray(value) ? value.join(", ") : String(value),
            )
            .filter(Boolean);
          if (errors.length > 0) {
            errorMessage = errors.join("\n");
            isValidationError = true;
          }
        }
      }

      Swal.fire({
        icon: isValidationError ? "warning" : "error",
        title: isValidationError ? "Atenção" : "Erro ao Salvar",
        text: errorMessage,
        confirmButtonText: "Entendi",
        confirmButtonColor: "#3b82f6",
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    agentInfo.name,
    agentInfo.businessType,
    editor,
    nodes,
    edges,
    id,
    api,
    navigate,
  ]);

  // Keyboard shortcuts
  useEffect(() => {
    // Ctrl+Z/Y do canvas não devem disparar quando o usuário está editando
    // texto em um input/textarea/select/contentEditable — o browser cuida do
    // undo/redo nativo do campo.
    const isEditableTarget = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el.isContentEditable
      );
    };

    const handleKeyDown = (e) => {
      const editable = isEditableTarget();

      if (!editable && (e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        const state = undo();
        if (state) {
          setNodes(state.nodes);
          setEdges(filterInvalidEdges(state.edges, state.nodes));
        }
      }
      if (
        !editable &&
        (e.ctrlKey || e.metaKey) &&
        (e.key === "y" || (e.key === "z" && e.shiftKey))
      ) {
        e.preventDefault();
        const state = redo();
        if (state) {
          setNodes(state.nodes);
          setEdges(filterInvalidEdges(state.edges, state.nodes));
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undo, redo, handleSave, filterInvalidEdges]);

  // Publish — toda a lógica de validação + erros vive em usePublishValidation.
  const handlePublish = async () => {
    if (!id) {
      await handleSave();
      return;
    }
    const result = await validateAndPublish(id, nodes, edges, {
      onLLMValidationFail: () => setShowEditorPanel(true),
    });
    if (result.success) {
      setSaveStatus("saved");
      setAgentInfo((prev) => ({ ...prev, isPublished: true }));
    }
  };

  const handleOpenChannels = () => {
    if (!id) return;
    setChannelModalMode("channels");
    setShowPublishModal(true);
  };

  const handlePublishSuccess = useCallback(() => {
    setSaveStatus("saved");
    setShowPublishModal(false);
    setAgentInfo((prev) => ({ ...prev, isPublished: true }));
  }, []);

  const renderTooltip = (text) => (props) => (
    <Tooltip {...props}>{text}</Tooltip>
  );

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <div className="afc-container">
      <Sidebar
        onDragStart={onDragStart}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        nodes={nodes}
        isProPlan={isProPlan}
        onProClick={(item) =>
          openUpgradeModal({
            featureName: item.label,
            featureDescription: item.description,
            featureIcon: item.icon,
            featureGradient: item.gradient,
          })
        }
      />

      <div className="afc-main" ref={reactFlowWrapper}>
        {/* Top Bar */}
        <div className="afc-topbar">
          <div className="afc-topbar-left">
            <button
              className="afc-back-btn"
              onClick={() => navigate("/agents-flow")}
            >
              <IconArrowLeft size={18} />
            </button>
            <div className="afc-agent-info">
              <input
                type="text"
                className="afc-agent-name"
                value={agentInfo.name}
                onChange={(e) =>
                  setAgentInfo((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Nome do agente"
              />
              {editor?.business_info?.business_name && (
                <span className="afc-agent-business">
                  {editor.business_info.business_name}
                </span>
              )}
            </div>
          </div>

          <div className="afc-topbar-center">
            <div className="afc-history-controls">
              <OverlayTrigger
                placement="bottom"
                overlay={renderTooltip("Desfazer (Ctrl+Z)")}
              >
                <button
                  className="afc-history-btn"
                  onClick={() => {
                    const state = undo();
                    if (state) {
                      setNodes(state.nodes);
                      setEdges(filterInvalidEdges(state.edges, state.nodes));
                    }
                  }}
                  disabled={!canUndo}
                >
                  <IconArrowBackUp size={18} />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={renderTooltip("Refazer (Ctrl+Y)")}
              >
                <button
                  className="afc-history-btn"
                  onClick={() => {
                    const state = redo();
                    if (state) {
                      setNodes(state.nodes);
                      setEdges(filterInvalidEdges(state.edges, state.nodes));
                    }
                  }}
                  disabled={!canRedo}
                >
                  <IconArrowForwardUp size={18} />
                </button>
              </OverlayTrigger>
            </div>
          </div>

          <div className="afc-topbar-right">
            {saveStatus === "saved" && (
              <span className="afc-save-status saved">
                <IconCheck size={16} /> Salvo
              </span>
            )}
            {saveStatus === "error" && (
              <span className="afc-save-status error">Erro ao salvar</span>
            )}
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="tooltip-user-guide">Guia Prático</Tooltip>}
            >
              <button
                className="afc-topbar-btn afc-topbar-btn--guide"
                onClick={() => setShowUserGuide(true)}
              >
                <IconBook2 size={20} />
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="tooltip-instructions">
                  Instruções do Agente
                </Tooltip>
              }
            >
              <button
                className="afc-topbar-btn afc-topbar-btn--instructions"
                onClick={() => setShowInstructionsModal(true)}
              >
                <IconFileText size={22} color="#fff" />
              </button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="tooltip-config">Configurações Avançadas</Tooltip>
              }
            >
              <button
                className="afc-topbar-btn afc-topbar-btn--config"
                onClick={() => setShowEditorPanel(true)}
              >
                <IconSettings size={20} />
              </button>
            </OverlayTrigger>
          </div>
        </div>

        {/* Canvas */}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChangeWithHistory}
          onEdgesChange={onEdgesChangeWithHistory}
          onConnect={onConnect}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          onNodeContextMenu={onNodeContextMenu}
          onPaneClick={closeContextMenu}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{
            type: "deletable",
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8" },
            style: { stroke: "#94a3b8", strokeWidth: 2 },
          }}
          fitView
          fitViewOptions={{
            maxZoom: 0.8,
            minZoom: 0.3,
            padding: 0.2,
          }}
          minZoom={0.1}
          maxZoom={1.2}
          snapToGrid
          snapGrid={[20, 20]}
          deleteKeyCode={["Backspace", "Delete"]}
          className="afc-flow"
          proOptions={{ hideAttribution: true }}
        >
          <Background
            variant="dots"
            gap={24}
            size={1}
            color="rgba(148, 163, 184, 0.3)"
          />
          <Controls showInteractive={false} className="afc-controls" />
          <MiniMap
            className="afc-minimap"
            nodeColor={(node) => {
              if (node.type === "start") return "#10b981";
              if (node.type === "end") return "#64748b";
              return "#3b82f6";
            }}
            maskColor="rgba(0, 0, 0, 0.05)"
          />

          {/* Floating Action Toolbar */}
          <Panel position="top-right" className="afc-floating-toolbar">
            <OverlayTrigger
              placement="left"
              overlay={renderTooltip("Salvar (Ctrl+S)")}
            >
              <button
                className="afc-toolbar-btn"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <Spinner size="sm" />
                ) : (
                  <IconDeviceFloppy size={20} />
                )}
              </button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="left"
              overlay={renderTooltip("Testar Agente")}
            >
              <button
                className={`afc-toolbar-btn ${simulator.isOpen ? "active" : ""}`}
                onClick={handleToggleSimulator}
                disabled={!id}
              >
                <IconPlayerPlay size={20} />
              </button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="left"
              overlay={renderTooltip("Publicar Agente")}
            >
              <button
                className="afc-toolbar-btn primary"
                onClick={handlePublish}
              >
                <IconRocket size={20} />
              </button>
            </OverlayTrigger>
          </Panel>

          <Panel position="bottom-center" className="afc-help-panel">
            <span>
              Arraste componentes • Clique para configurar • Botão direito para
              excluir
            </span>
          </Panel>
        </ReactFlow>

        {/* Context Menu */}
        {contextMenu && (
          <div
            className="afc-context-menu"
            style={{
              position: "fixed",
              top: contextMenu.y,
              left: contextMenu.x,
              zIndex: 1000,
            }}
          >
            <div className="afc-context-menu-header">
              {contextMenu.nodeLabel}
            </div>
            <button
              className="afc-context-menu-item danger"
              onClick={handleDeleteNode}
            >
              <IconTrash size={16} />
              Excluir
            </button>
          </div>
        )}
      </div>

      {/* Simulator Panel */}
      <div
        className={`sim-panel ${simulator.isOpen && id ? "sim-panel--open" : ""}`}
      >
        {simulator.isOpen && state.currentAgent && (
          <AgentSimulator
            agent={state.currentAgent}
            onClose={() => toggleSimulator(false)}
          />
        )}
      </div>

      <ConfigPanel
        node={selectedNode}
        nodes={nodes}
        onClose={() => setSelectedNode(null)}
        onUpdate={updateNodeData}
      />

      {/* Agent Editor Panel */}
      <AgentEditorPanel
        show={showEditorPanel}
        onHide={() => setShowEditorPanel(false)}
        agentId={id}
      />

      {/* Modal de escolha */}
      <AgentChoiceModal
        show={showChoiceModal}
        onBack={() => navigate(-1)}
        onChooseScratch={() => {
          setShowChoiceModal(false);
          setShowWelcomeModal(true);
        }}
        onChooseWizard={() => {
          setShowChoiceModal(false);
          setShowWizard(true);
        }}
      />

      {/* Wizard */}
      <AgentCreationWizard
        show={showWizard}
        onComplete={handleAgentCreated}
        onSkip={() => {
          setShowWizard(false);
          setShowWelcomeModal(true);
        }}
      />

      {/* Welcome Modal */}
      <WelcomeAgentModal
        show={showWelcomeModal}
        onAgentCreated={handleAgentCreated}
        createAgent={api.createAgent}
        onBack={() => {
          setShowWelcomeModal(false);
          setShowChoiceModal(true);
        }}
      />

      {/* Publish Channel Modal */}
      <PublishChannelModal
        show={showPublishModal}
        onHide={() => setShowPublishModal(false)}
        agentId={id}
        agentName={agentInfo.name}
        wabottyAccount={agentInfo.wabottyAccount}
        onPublishSuccess={handlePublishSuccess}
        mode={channelModalMode}
        nodes={nodes}
      />

      {/* PRO Upgrade Modal */}
      <UpgradeModal
        show={upgradeModal.show}
        onClose={closeUpgradeModal}
        featureName={upgradeModal.featureName}
        featureDescription={upgradeModal.featureDescription}
        featureIcon={upgradeModal.featureIcon}
        featureGradient={upgradeModal.featureGradient}
      />

      {/* Agent Instructions Modal */}
      <AgentInstructionsModal
        show={showInstructionsModal}
        onHide={() => setShowInstructionsModal(false)}
        agentId={id}
        agentData={{
          identity_config: editor.identity_config,
          business_info: editor.business_info,
          behavior_config: editor.behavior_config,
          custom_messages:
            editor.custom_messages || state.currentAgent?.custom_messages || {},
        }}
        onSave={(updatedCustomMessages) => {
          setEditorData({ custom_messages: updatedCustomMessages });
        }}
      />

      {/* User Guide Modal */}
      <UserGuideModal
        show={showUserGuide}
        onHide={() => setShowUserGuide(false)}
      />
    </div>
  );
};

export default CanvasInner;
